import { getCountryFormDefinition } from "CMS/utils";
import FormattedAddress from "components/Atoms/FormattedAddress";
import { FormSection, InPageEditableSectionField } from "components/MainSectionTemplate/types";
import { CompanySidePanel } from "components/SidePanel/Company/CompanySidePanel";
import React from "react";
import { isNullOrEmptyString, newLineToBr } from "utilities";
import { isFieldHidden } from "./utils";
import PhoneRow from "components/DetailPage/PhoneRow";
import WebsiteLink from "components/Website/WebsiteLink";
import VatNumberDetails from "components/VatNumber/VatNumberDetails";
import { findEnumObject } from "utilities/enumutils";
import Doors from "venue/components/Doors";
import Stage from "venue/components/Stage";
import { PhosphorWarning } from "components/Atoms/PhosphorIcon";
import { Message } from "semantic-ui-react";
import CompanyMergeModal from "company/detail/merge/app";
import AreYouSureModal from "components/modals/AreYouSureModal";
import { DeleteDialog } from "components";
import { mapShowInfoToUpdateShowInternalInstruction, mapShowToShowInfo } from "show/edit/utils";

export const checkVenueCompanySection = (
  company: server.dto.Company,
  linkedContact: server.dto.Contact,
  venue: server.dto.Venue,
  formBehaviour: server.dto.WebformFunctionGroupCompanyFormBehavior,
  venueFormBehaviour: server.dto.WebformFunctionGroupVenueFormBehavior,
  show: server.dto.Show,
  numberOfSimilarCompanies: number
): FormSection => ({
  key: "checkVenue",
  type: "regular",
  hidden: company == null,
  title: <div className="flex justify-space-between">
    {site.currentEnvironment.functionGroups.venue.singular} - {RESX.Company.resxCompany}
    {!company?.isValidated && <PhosphorWarning iconSize="large" />}
  </div>,
  fields: company == null ? [] : [
    ...checkCompanyFields(company, formBehaviour),
    ...checkVenueFields(venue.details, venueFormBehaviour), {
      key: "save",
      label: "",
      render: () => <CheckCompanyComponent
        company={company}
        linkedContact={linkedContact}
        show={show}
        numberOfSimilarCompanies={numberOfSimilarCompanies}
        type="venue"
      />,
    }
  ]
});

export const checkPromoterCompanySection = (
  company: server.dto.Company,
  linkedContact: server.dto.Contact,
  formBehaviour: server.dto.WebformFunctionGroupCompanyFormBehavior,
  show: server.dto.Show,
  numberOfSimilarCompanies: number
): FormSection => ({
  key: "checkPromoter",
  type: "regular",
  hidden: company == null,
  title: <div className="flex justify-space-between">
    {site.currentEnvironment.functionGroups.promoter.singular} - {RESX.Company.resxCompany}
    {!company?.isValidated && <PhosphorWarning iconSize="large" />}
  </div>,
  fields: company == null ? [] : [
    ...checkCompanyFields(company, formBehaviour), {
      key: "save",
      label: "",
      render: () => <CheckCompanyComponent
        company={company}
        linkedContact={linkedContact}
        show={show}
        numberOfSimilarCompanies={numberOfSimilarCompanies}
        type="promoter"
      />,
    }
  ],
});

const checkCompanyFields = (
  company: server.dto.Company,
  formBehaviour: server.dto.WebformFunctionGroupCompanyFormBehavior,
): Array<InPageEditableSectionField> => {
  const formDefinition = getCountryFormDefinition(company.admAddress.country);

  return [{
    key: "name",
    label: RESX.GeneralLabels.Name,
    isEmpty: !company.name,
    render: () => <CompanySidePanel
      companyId={company.companyId}
      trigger={<a>{company.name}</a>}
    />,
  }, {
    key: "address",
    label: RESX.Address.resxAddress,
    render: () => <FormattedAddress address={company.address} />,
  }, {
    key: "phone",
    label: RESX.Company.Phone,
    render: () => <PhoneRow numbers={company.landlineNumbers} />,
    isEmpty: company.landlineNumbers.length === 0,
    hidden: isFieldHidden(formBehaviour.phone),
  }, {
    key: "website",
    label: RESX.GeneralLabels.Website,
    render: () => <WebsiteLink website={company.website} />,
    isEmpty: isNullOrEmptyString(company.website),
    hidden: isFieldHidden(formBehaviour.website),
  }, {
    key: "facebook",
    label: "Facebook",
    render: () => <WebsiteLink website={company.facebook} />,
    isEmpty: isNullOrEmptyString(company.facebook),
    hidden: isFieldHidden(formBehaviour.facebook),
  }, {
    key: "euVatNumber",
    label: formDefinition.euVatNumber.label,
    sublabel: formDefinition.euVatNumber.sublabel,
    render: () => <VatNumberDetails companyId={company.companyId} euVatNumber={company.euVatNumber} />,
    isEmpty: !company.euVatNumber,
    hidden: !formDefinition.euVatNumber.displayInUI || isFieldHidden(formBehaviour.taxNumber),
  }, {
    key: "fiscalNumber1",
    label: formDefinition.fiscalNumber1.label,
    sublabel: formDefinition.fiscalNumber1.sublabel,
    render: () => <div>{company.fiscalNumber1}</div>,
    isEmpty: isNullOrEmptyString(company.fiscalNumber1),
    hidden: !formDefinition.fiscalNumber1.displayInUI || isFieldHidden(formBehaviour.taxNumber),
  }, {
    key: "businessRegistration",
    label: formDefinition.businessRegistrationNumber.label,
    sublabel: formDefinition.businessRegistrationNumber.sublabel,
    render: () => <div>{company.businessRegistrationNumber}</div>,
    isEmpty: isNullOrEmptyString(company.businessRegistrationNumber),
    hidden: isFieldHidden(formBehaviour.businessRegistrationNumber),
  }, {
    key: "additional",
    label: RESX.Company.AdditionalInformation,
    render: () => <div>{newLineToBr(company.additional)}</div>,
    isEmpty: isNullOrEmptyString(company.additional),
    hidden: !formBehaviour.additional,
  }]
}

const checkVenueFields = (
  venueDetails: server.dto.VenueDetails,
  venueFormBehaviour: server.dto.WebformFunctionGroupVenueFormBehavior,
): Array<InPageEditableSectionField> => {
  return [{
    key: "capacity",
    label: RESX.Venue.Capacity,
    render: () => <div>{venueDetails.capacity} PAX</div>,
    isEmpty: !venueDetails.capacity,
    hidden: isFieldHidden(venueFormBehaviour.capacity),
  }, {
    key: "ageRestriction",
    label: RESX.Venue.AgeRestrictions,
    render: () => <div>{findEnumObject(venueDetails.ageRestriction, serverReference.ageRestrictions).text}</div>,
    isEmpty: venueDetails.ageRestriction === "unknown",
    hidden: isFieldHidden(venueFormBehaviour.ageRestrictions),
  }, {
    key: "doors",
    label: RESX.Venue.Doors,
    render: () => <Doors doors={venueDetails.doors} />,
    isEmpty: !venueDetails.doors || (!venueDetails.doors.open && !venueDetails.doors.close),
    hidden: isFieldHidden(venueFormBehaviour.doors),
  }, {
    key: "category",
    label: RESX.Venue.Category,
    render: () => <div>{findEnumObject(venueDetails.category, serverReference.venueCategories).text}</div>,
    isEmpty: venueDetails.category === "unknown",
    hidden: isFieldHidden(venueFormBehaviour.category),
  }, {
    key: "stage",
    label: RESX.Venue.Stage,
    render: () => <Stage stage={venueDetails.stage} />,
    isEmpty: !venueDetails.stage || (!venueDetails.stage.depth && !venueDetails.stage.width && !venueDetails.stage.toFloor && !venueDetails.stage.toCeiling),
    hidden: isFieldHidden(venueFormBehaviour.stage),
  }, {
    key: "vehicleAccess",
    label: RESX.Venue.VehicleAccess,
    render: () => <div>{venueDetails.vehicleAccess}</div>,
    isEmpty: isNullOrEmptyString(venueDetails.vehicleAccess),
    hidden: isFieldHidden(venueFormBehaviour.vehicleAccess),
  }, {
    key: "parking",
    label: RESX.Venue.Parking,
    render: () => <div>{venueDetails.parking}</div>,
    isEmpty: isNullOrEmptyString(venueDetails.parking),
    hidden: isFieldHidden(venueFormBehaviour.parking),
  }, {
    key: "gearAccess",
    label: RESX.Venue.GearAccess,
    render: () => <div>{venueDetails.gearAccess}</div>,
    isEmpty: isNullOrEmptyString(venueDetails.gearAccess),
    hidden: isFieldHidden(venueFormBehaviour.gearAccess),
  }, {
    key: "power",
    label: RESX.Venue.Power,
    render: () => <div>{venueDetails.power}</div>,
    isEmpty: isNullOrEmptyString(venueDetails.power),
    hidden: isFieldHidden(venueFormBehaviour.power),
  }, {
    key: "lighting",
    label: RESX.Venue.Lighting,
    render: () => <div>{venueDetails.lighting}</div>,
    isEmpty: isNullOrEmptyString(venueDetails.lighting),
    hidden: isFieldHidden(venueFormBehaviour.lighting),
  }, {
    key: "dressingRooms",
    label: RESX.Venue.DressingRooms,
    render: () => <div>{venueDetails.dressingRooms}</div>,
    isEmpty: isNullOrEmptyString(venueDetails.dressingRooms),
    hidden: isFieldHidden(venueFormBehaviour.dressingRooms),
  }, {
    key: "noiseRestrictions",
    label: RESX.Venue.NoiseRestrictions,
    render: () => <div>{venueDetails.noiseRestrictions}</div>,
    isEmpty: isNullOrEmptyString(venueDetails.noiseRestrictions),
    hidden: isFieldHidden(venueFormBehaviour.noiseRestrictions),
  }]
}

interface CheckCompanyComponentProps {
  company: server.dto.Company;
  linkedContact: server.dto.Contact;
  show: server.dto.Show;
  numberOfSimilarCompanies: number;
  type: "venue" | "promoter";
}

export const CheckCompanyComponent = ({
  company, linkedContact, show, numberOfSimilarCompanies, type
}: CheckCompanyComponentProps) => {
  const [isAdding, setIsAdding] = React.useState(false);
  const [isMerging, setIsMerging] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const removeCompany = async () => {
    const instruction = mapShowInfoToUpdateShowInternalInstruction(mapShowToShowInfo(show), show.production);
    const updateRes = await window.CoreApi.Show.UpdateForInternalEnvironment({
      ...instruction,
      showId: show.showId,
      venueCompanyId: type === "venue" ? null : instruction.venueCompanyId,
      venueContactId: type === "venue" ? null : instruction.venueContactId,
      promoterCompanyId: type === "promoter" ? null : instruction.promoterCompanyId,
      promoterContactId: type === "promoter" ? null : instruction.promoterContactId,
    })

    if (updateRes.data.hasErrors) {
      window.displayGenericModal(RESX.ExceptionMessage.ThereIsAProblem, updateRes.data.errors[0].message, "tiny", "red");
      return;
    }

    if (!company.isValidated) {
      await window.CoreApi.Company.Delete(company.companyId);
    }

    if (linkedContact != null && !linkedContact.isValidated) {
      await window.CoreApi.Contact.Delete(linkedContact.contactId);
    }

    window.location.reload();
  }

  const canMerge = !company.isValidated && company.permissions.canMerge;
  const canRemoveCompany = !company.isValidated && show.permissions.canModify;
  const displayActions = canMerge || canRemoveCompany;

  return <>
    {isDeleting && <DeleteDialog
      isOpen
      message={RESX.GeneralWarnings.DeleteSure}
      onDelete={removeCompany}
      handleClose={() => setIsDeleting(false)}
      spinForever
    />}
    {isAdding && <AreYouSureModal
      title={RESX.Buttons.Add}
      yesButtonLabel={RESX.Buttons.Add}
      yesButtonColor="green"
      onClose={() => { setIsAdding(false); }}
      onYes={async () => window.CoreApi.Company.Validate({ companyId: company.companyId }).then(() => { window.location.reload(); })}
    />}
    <CompanyMergeModal
      webformId={show.showId}
      isOpen={isMerging}
      companyId={company.companyId}
      companyName={company.name}
      onClose={() => { setIsMerging(false); }}
    />
    <div className="flex-column gap grow1">
      {(!company.isValidated && !company.permissions.canMerge) && <Message color="red">
        {RESX.Webform.YouHaveInsufficientAccessToCompanySection}
      </Message>}
      {displayActions && <div>
        {canMerge && <>
          <div>
            <a onClick={() => setIsMerging(true)}>{RESX.Company.MergeWithACompanyThatIsAlreadyInTheDatabase}</a>
            <div className="text-sublabel">{RESX.Company.NumberOfCompaniesFoundInDatabaseThatAreSimilarTo} {company.name}: {numberOfSimilarCompanies}</div>
          </div>
          <div>
            <a onClick={() => setIsAdding(true)}>{RESX.GeneralLabels.AddToDatabase}</a>
            <div className="text-sublabel">{RESX.Webform.IfThisCompanyDoesntExists}</div>
          </div>
        </>}
        {canRemoveCompany && <div>
          <a onClick={() => setIsDeleting(true)}>{RESX.Buttons.btnDelete}</a>
          <div className="text-sublabel">{RESX.Webform.RemoveInformationIfThisCompanyShouldnt}</div>
        </div>}
      </div>}
    </div>
  </>
}